import { Component, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { RaiseService } from '../../../core/services/api/raise.service';
import { PopupService } from '../../../shared/services/popup/popup.service';
import { Router } from '@angular/router';
import { UnregisteredSubmissionStatus } from 'src/app/core/enum/unregistered-form-submission-status.enum';
import { TranslateService } from '@ngx-translate/core';
import { Role } from 'src/app/core/enum/role.enum';
import { DefaultEmailPattern } from '../../../core/services/constant';
import { finalize, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../../../environments/environment';

interface OtpVerificationResponse {
    viewToken?: string;
    submissionToken?: string;
    resetPasswordToken?: string;
    status?: string;
}

@Component({
  selector: 'app-unregistered-submission-list-modal',
  templateUrl: './unregistered-submission-list-modal.component.html',
  styleUrls: ['./unregistered-submission-list-modal.component.scss'],
  standalone: false
})

export class UnregisteredSubmissionListModalComponent implements OnDestroy {
  email: string = '';
  otp: string = '';
  isLoading: boolean = false;
  isSubmittingOverride: boolean = false;
  step: 'email' | 'otp' | 'status' | 'emailExisting' = 'email';
  submission: any = null;
  private verificationToken: string | null = null;
  private submissionToken: string | null = null;
  private resetPasswordToken: string | null = null;
  intent: 'continue' | 'check' = 'continue';
  countdown: number = 0;
  emailErrorKey: string = '';
  isEmailValid: boolean = false;
  private interval: ReturnType<typeof setInterval>;
  private destroy$ = new Subject<void>();

  UnregisteredSubmissionStatus: typeof UnregisteredSubmissionStatus = UnregisteredSubmissionStatus;

  get escapedEmail(): string {
    return this.email
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  statusBadgeClass = {
    [UnregisteredSubmissionStatus.Pending]: "badge-warning",
    [UnregisteredSubmissionStatus.Rejected]: "badge-danger",
    [UnregisteredSubmissionStatus.Approved]: "badge-success",
    [UnregisteredSubmissionStatus.ApprovedPendingActivation]: "badge-blue",
    [UnregisteredSubmissionStatus.Success]: "badge-success",
  }

  constructor(
    public activeModal: NgbActiveModal,
    private raiseService: RaiseService,
    private popupService: PopupService,
    private router: Router,
    private translateService: TranslateService
  ) { }

  ngOnDestroy() {
    clearInterval(this.interval);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private extractBackendMessage(err: any): string {
    if (typeof err === 'string') {
      return err;
    }
    return (
      err?.Error?.Message ??
      err?.error?.Error?.Message ??
      err?.error?.message ??
      err?.error?.error?.message ??
      err?.ErrorMessage ??
      ''
    );
  }

  validateEmail(showRequiredError: boolean = true): boolean {
    const trimmedEmail = (this.email || '').trim();

    if (!trimmedEmail) {
      this.emailErrorKey = showRequiredError ? 'raise.emailRequired' : '';
      this.isEmailValid = false;
      return false;
    }

    if (!environment.emailAllowPlusSymbol && trimmedEmail.includes('+')) {
      this.emailErrorKey = 'unregisteredModal.emailPlusNotAllowed';
      this.isEmailValid = false;
      return false;
    }

    const emailRegex = DefaultEmailPattern;

    if (!emailRegex.test(trimmedEmail)) {
      this.emailErrorKey = 'signUp.emailFormatIncorrect';
      this.isEmailValid = false;
      return false;
    }

    this.emailErrorKey = '';
    this.isEmailValid = true;
    return true;
  }

  onEmailChange(value: string): void {
    this.email = value || '';

    // false means: don't show "email required" while user is just typing/clearing
    this.validateEmail(false);
  }

  submitEmail(intent: 'continue' | 'check') {
    if (!this.validateEmail()) return;

    this.email = this.email.trim();
    this.intent = intent;
    this.sendOtp();
  }

  startCountdown() {
    this.countdown = 28;
    clearInterval(this.interval);
    this.interval = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        clearInterval(this.interval);
      }
    }, 1000);
  }

  sendOtp() {
    if (!this.validateEmail()) return;
    this.email = this.email.trim();
    this.sendOtpRequest().subscribe({
      next: () => {
        this.step = 'otp';
        this.startCountdown();
      },
      error: (err) => {
        const backendMessage = this.extractBackendMessage(err);
        if (backendMessage === 'ERR_USER_NOT_FOUND') {
          this.activeModal.dismiss();
          this.popupService.alert('raise.emailNotFound');
        } else if (backendMessage === 'ERR_USER_ALREADY_EXISTS') {
          this.step = 'emailExisting';
        } else {
          this.popupService.alert('common.unknownError');
        }
      }
    });
  }

  resendOtp() {
    if (!this.validateEmail()) return;
    this.email = this.email.trim();
    this.sendOtpRequest().subscribe({
      next: () => this.startCountdown(),
      error: () => this.popupService.alert('common.unknownError')
    });
  }

  private sendOtpRequest(): Observable<any> {
    this.isLoading = true;
    return this.raiseService.sendUnregisteredOTP(this.email).pipe(
      takeUntil(this.destroy$),
      finalize(() => (this.isLoading = false))
    );
  }

  verifyOtp() {
    if (!this.otp) return;

    this.isLoading = true;
    this.raiseService.verifyUnregisteredOTP(this.email, this.otp).pipe(takeUntil(this.destroy$)).subscribe(
      (res: OtpVerificationResponse) => {
            this.verificationToken = res?.viewToken;
            this.submissionToken = res?.submissionToken;
            this.resetPasswordToken = res?.resetPasswordToken;

            if (res?.status === 'AccountExists') {
                this.isLoading = false;
                this.step = 'emailExisting';
                return;
            }

            // Pending activation or normal unregistered submission
            this.fetchSubmission();
      },
      (err) => {
        this.isLoading = false;
        // The API returns the literal BadRequest string for a wrong/expired OTP; the older
        // ERR_OTP_* codes are kept for compatibility. Anything else (e.g. this same endpoint's
        // ERR_INVALID_EMAIL_FORMAT for a plus-sign email, or a network/5xx failure) must stay on
        // the generic message rather than being mislabelled as a bad OTP.
        const backendMessage = this.extractBackendMessage(err);
        if (
          backendMessage === 'ERR_OTP_INVALID' ||
          backendMessage === 'ERR_OTP_EXPIRED' ||
          backendMessage === 'Invalid or expired OTP.'
        ) {
          this.popupService.alert('raise.otpInvalid');
        } else {
          this.popupService.alert('common.unknownError');
        }
      }
    );
  }

  goToSubmitApplication() {
    this.activeModal.close();
    this.router.navigate(['/raise/pre-registration'], {
        state: {
            prefillData: { email: this.email },
            submissionToken: this.submissionToken,
            viewToken: this.verificationToken
        }
    });
  }

  private fetchSubmission() {
    this.raiseService.getUnregisteredSubmissionsByEmail(this.email, this.verificationToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any[]) => {
          this.isLoading = false;
          // User can only have one submission - take the first (most recent)
          this.submission = res && res.length > 0 ? res[0] : null;

          // FRC-1023: Active accounts use the same Account Active popup,
          // regardless of whether the account was registered directly
          // or created after an approved application.
          if (this.submission?.status === UnregisteredSubmissionStatus.Success) {
            this.step = 'emailExisting';
            return;
          }

          // If user clicked 'continue' and has no existing submission, go directly to form
          if (this.intent === 'continue' && !this.submission) {
            this.goToSubmitApplication();
          } else {
            // Show status view (existing submission found, or user clicked 'check')
            this.step = 'status';
          }
        },
        (err) => {
          this.isLoading = false;
          const backendMessage = this.extractBackendMessage(err);
          if (backendMessage === 'ERR_USER_NOT_FOUND') {
            this.submission = null;
            if (this.intent === 'continue') {
              this.goToSubmitApplication();
            } else {
              this.step = 'status';
            }
          } else {
            this.popupService.alert('common.unknownError');
          }
        }
      );
  }

  viewMyApplication() {
    this.activeModal.close();
    this.router.navigate(['/raise/pre-registration'], {
      state: {
        prefillData: this.submission,
        submissionToken: this.submissionToken,
        viewOnly: true
      }
    });
  }

  submitNewApplicationOverride() {
    if (this.isSubmittingOverride) {
      return;
    }

    if (!this.submission?.id || !this.email || !this.verificationToken) {
      console.error('Override blocked: missing submission id/email/verification token', {
        hasId: !!this.submission?.id,
        hasEmail: !!this.email,
        hasToken: !!this.verificationToken
      });
      this.popupService.alert('common.unknownError');
      return;
    }

    this.isSubmittingOverride = true;
    this.raiseService
      .getUnregisteredSubmissionById(
        this.submission.id,
        this.email,
        this.verificationToken
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (detail) => {
          this.isSubmittingOverride = false;

          if (!detail) {
            this.popupService.alert('common.unknownError');
            return;
          }

          const detailPrefill = Object.fromEntries(
            Object.entries(detail).filter(([, value]) => value != null)
          );

          const originalTemplateId =
            detail?.dynamicFormTemplateId ??
            detail?.DynamicFormTemplateId ??
            this.submission?.dynamicFormTemplateId ??
            this.submission?.DynamicFormTemplateId ??
            null;

          if (originalTemplateId == null) {
            this.popupService.alert('common.unknownError');
            return;
          }

          const content =
            detail?.content ||
            detail?.Content ||
            this.submission?.content ||
            this.submission?.Content ||
            null;
          const ecfContent =
            detail?.ecfContent ||
            detail?.ECFContent ||
            this.submission?.ecfContent ||
            this.submission?.ECFContent ||
            null;

          if (!content && !ecfContent) {
            this.popupService.alert('common.unknownError');
            return;
          }

          this.navigateToOverride(
            {
              ...this.submission,
              ...detailPrefill,
              content,
              ecfContent,
              email: this.email
            },
            originalTemplateId
          );
        },
        error: (err) => {
          this.isSubmittingOverride = false;
          const backendMessage = this.extractBackendMessage(err);
          this.popupService.alert(backendMessage || 'common.unknownError');
        }
      });
  }

  private navigateToOverride(prefillData: any, originalTemplateId: number | null = null): void {
    this.activeModal.close();
    this.router.navigate(['/raise/pre-registration'], {
      state: {
        prefillData,
        submissionToken: this.submissionToken,
        viewToken: this.verificationToken,
        originalTemplateId,
        overrideExisting: true
      }
    });
  }

  activateAccount() {
    this.activeModal.close();
    this.router.navigate([`/sign-up/register/${Role.IssuerRep}`], {
      state: {
        prefillData: this.submission,
        isActivation: true,
        resetPasswordToken: this.resetPasswordToken
      }
    });
  }

  goToLogin() {
    this.activeModal.close();
    this.router.navigate(['/login']);
  }

  goToForgotPassword() {
    this.activeModal.close();
    this.router.navigate(['/forgot-password']);
  }
}
